import { Button, FilledInput, Modal } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PuiTypography from "../../../components/elements/PuiTypography";
import { useInitFlag } from "../../../components/function/useInitFlag";
import { useInitText } from "../../../components/function/useInitText";
import PuiMessage from "../../../components/widgets/PuiMessage";
import { AppState } from "../../../root/RootReducer";
import { RenameFootage } from "../../../studio/store/actions/DirectoryActions";
import { RenameTemplate } from "../../../studio/store/actions/TemplateActions";
import { RenameTimeline, SetIsHandleKeyboard } from "../../../studio/store/actions/TimelineActions";
import styles from "./RenameObjectModal.module.css";

export type objectType = "プロジェクト" | "テンプレート" | "フッテージ";

export interface Object {
    id: number;
    name: string;
}

interface RenameObjectModalProps {
    isOpenModal: boolean;
    setOpenModal: (target: boolean) => void;
    id: number;
    editName: string;
    setEditName: (name: string) => void;
    type: objectType;
}

function RenameObjectModal({
    isOpenModal,
    setOpenModal,
    id,
    editName,
    setEditName,
    type,
}: RenameObjectModalProps) {
    const dispatch = useDispatch();

    // Storeからデータ取得
    const { user, isLoading, timelineInfoList, templateList, footageList } = useSelector(
        (state: AppState) => ({
            user: state.user.user,
            isLoading: state.directory.isLoading,
            timelineInfoList: state.timeline.timelineInfoList,
            templateList: state.template.templateList,
            footageList: state.directory.footageList,
        })
    );

    // フッテージ名入力処理
    const [name, setName] = useInitText();
    // 名前重複チェックフラグ
    const [isAlreadyExistedError, setIsAlreadyExistedError] = useInitFlag();
    // 重複する名前
    const [errorName, setErrorName] = useInitText();
    // 変更ボタン押下すチェックフラグ
    const [isRenameObject, setIsRenameObject] = useState(false);
    // モーダル表示・非表示フラグ
    const [closeModal, isCloseModal] = useState(false);

    // モーダルを閉じる
    const handleCloseModal = () => {
        dispatch(SetIsHandleKeyboard(true)); // 編集する際に、キーボードによるvideoのコントロールを無効にする
        isCloseModal(false);
        setOpenModal(false);
        setIsRenameObject(false);
    };

    const handleCloseError = () => {
        setIsAlreadyExistedError(false);
        setErrorName("");
    };

    // 拡張子なしのファイル名を設定する。
    const removeExtension = (filename: string) => {
        const lastDotPosition = filename.lastIndexOf(".");
        if (lastDotPosition === -1) return filename;
        else return filename.substring(0, lastDotPosition);
    };

    // 入力した名前の設定
    const handleChangeName = (event: any) => {
        setName(event.target.value);
    };

    // 変更ボタン押す処理
    const handleClickRenameObject = () => {
        let current: any = null;
        switch (type) {
            case "プロジェクト":
                if (timelineInfoList && timelineInfoList.length > 0) {
                    current = timelineInfoList.find(
                        (timeline) => timeline.name === name && timeline.id !== id
                    );
                }
                break;

            case "テンプレート":
                if (templateList && templateList.length > 0) {
                    current = templateList.find(
                        (template) => template.name === name && template.id !== id
                    );
                }
                break;

            case "フッテージ":
                if (footageList && footageList.length > 0) {
                    current = footageList.find(
                        (footage) => removeExtension(footage.name) === name && footage.id !== id
                    );
                }
                break;
        }
        if (current) {
            setErrorName(name);
            setIsAlreadyExistedError(true);
        } else {
            renameObject();
        }
    };

    // 名前変更処理
    const renameObject = () => {
        if (user) {
            switch (type) {
                case "プロジェクト":
                    // プロジェクト名変更APIコール
                    dispatch(RenameTimeline(id, name, Number(user.id)));
                    break;

                case "テンプレート":
                    // テンプレート名変更APIコール
                    dispatch(RenameTemplate(id, name, Number(user.id)));
                    break;

                case "フッテージ":
                    // フッテージ名変更APIコール
                    dispatch(RenameFootage(id, name, Number(user.id)));
            }
        }
        // 入力値リセット
        setName("");
        // 編集値リセット
        setEditName("");
        setIsRenameObject(true);
    };

    // フッテージ名変更するの場合、フッテージ名テキストボックスの初期表示
    // モーダルが閉じる時、フッテージ名をリセット
    useEffect(() => {
        if (isOpenModal) {
            if (editName) setName(removeExtension(editName));
        } else {
            setName("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenModal]);

    // 名前変更して後、ポップアップを非表示する。
    useEffect(() => {
        if (!isLoading && isRenameObject) {
            // モーダルを閉じる
            handleCloseModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isRenameObject]);

    // ダイアログボックスの非表示後、フッテージ作成・フッテージ名変更モーダル非表示
    useEffect(() => {
        if (closeModal) {
            handleCloseModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closeModal]);

    return (
        <>
            <Modal
                open={isOpenModal}
                onClose={handleCloseModal}
                className={styles.createFolderModal}
            >
                <div className={styles.createFolderDiv}>
                    <PuiTypography>{type + "名の編集"}</PuiTypography>
                    <div className={styles.inputArea}>
                        <FilledInput
                            value={name}
                            id="name"
                            placeholder={type + "名を入力してください"}
                            autoComplete="off"
                            disableUnderline={true}
                            className={styles.folderCreateInputBox}
                            onChange={handleChangeName}
                        />
                        <Button
                            disabled={name.length === 0 || removeExtension(editName) === name}
                            className={styles.createrFolderBtn}
                            onClick={handleClickRenameObject}
                        >
                            変更
                        </Button>
                    </div>
                </div>
            </Modal>
            <PuiMessage
                open={isAlreadyExistedError}
                type="warning"
                title={errorName + "はすでに使用されています。別の名前に変更しください。"}
                duration={5000}
                onClose={handleCloseError}
            />
        </>
    );
}

export default RenameObjectModal;
